import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Button, Heading, Table, Thead, Tbody, Tr, Th, Td, Badge,
  Text, VStack, HStack, Spinner, useToast,
  FormControl, FormLabel, Switch,
  Stat, StatLabel, StatNumber, Input, Flex, Select
} from "@chakra-ui/react";
import { useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';



const ManualCPTDashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [mismatchPatterns, setMismatchPatterns] = useState([]);
  const [currentMismatches, setCurrentMismatches] = useState(0);
  const [accuracyPercentage, setAccuracyPercentage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showMatches, setShowMatches] = useState(true);
  const [matchMode, setMatchMode] = useState('exact'); // 'exact' or 'level'
  
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [recentConfigs, setRecentConfigs] = useState([]);
  const [configIdFilter, setConfigIdFilter] = useState('');
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const filteredAndSortedData = useMemo(() => {
    return dashboardData
      .filter(item => {
        const currentStatusKey = `match_status_${matchMode}`;
        const status = item[currentStatusKey];
        
        // If showMatches is true, show everything.
        // If false, hide 'Match' and 'Level Match'.
        if (showMatches) return true;
        return status !== 'Match' && status !== 'Level Match';
      })
      .sort((a, b) => {
        const codeA = a.ai_codes && a.ai_codes.length > 0 ? a.ai_codes[0] : '';
        const codeB = b.ai_codes && b.ai_codes.length > 0 ? b.ai_codes[0] : '';
        return codeA.localeCompare(codeB);
      });
  }, [dashboardData, showMatches, matchMode]);

  // Recalculate stats when data or match mode changes
  useEffect(() => {
    if (dashboardData.length === 0) {
      // Reset stats if there's no data
      setCurrentMismatches(0);
      setAccuracyPercentage(0);
      return;
    }

    let mismatchCount = 0;
    dashboardData.forEach(row => {
      const currentStatusKey = `match_status_${matchMode}`;
      const matchStatus = row[currentStatusKey];

      const isMismatchPending = 
        (matchStatus === 'Mismatch' || matchStatus === 'Partial Match') && 
        row.status === 'Pending Review';
      
      const isManualCorrect = row.review_outcome === 'manual_correct';

      if (isMismatchPending || isManualCorrect) {
        mismatchCount++;
      }
    });

    setCurrentMismatches(mismatchCount);

    const total = dashboardData.length;
    if (total > 0) {
      const accuracy = ((total - mismatchCount) / total) * 100;
      setAccuracyPercentage(Math.round(accuracy * 10) / 10); // Round to one decimal place
    } else {
      setAccuracyPercentage(0);
    }
  }, [dashboardData, matchMode]);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      if (!accessToken) return;
      try {
        const response = await callApi('/clients', accessToken);
        if (response.ok) {
          const data = await response.json();
          setClients(data.clients || []);
        }
      } catch (error) {
        console.error("Failed to fetch clients", error);
        toast({ title: "Error", description: "Failed to load clients", status: "error" });
      }
    };
    fetchClients();
  }, [accessToken, toast]);

  // Handle preselected state from navigation
  useEffect(() => {
    const initFromState = async () => {
      if (accessToken && location.state?.preselectedClientId) {
        const clientId = location.state.preselectedClientId;
        setSelectedClientId(clientId);
        
        // Fetch configs for this client
        const configs = await fetchRecentConfigs(clientId);
        
        if (location.state.preselectedConfigId) {
          const configId = location.state.preselectedConfigId.toString();
          setConfigIdFilter(configId);
          fetchData(configId);
        }
      }
    };
    
    initFromState();
  }, [accessToken, location.state]);

  const fetchRecentConfigs = async (clientId) => {
    try {
      const response = await callApi(`/api/manual-coding/recent-configurations?client_id=${clientId}`, accessToken);
      if (response.ok) {
        const data = await response.json();
        setRecentConfigs(data);
        return data;
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch recent configurations", error);
      toast({ title: "Error", description: "Failed to load configurations", status: "error" });
      return [];
    }
  };

  const fetchData = async (overrideConfigId = null) => {
    setLoading(true);
    // Clear mismatch patterns on new fetch
    setMismatchPatterns([]);
    
    try {
      let url = '/api/manual-coding/dashboard';
      const configIdToUse = overrideConfigId !== null ? overrideConfigId : configIdFilter;
      
      if (configIdToUse) {
        url += `?config_id=${configIdToUse}`;
      } else {
        // If no config ID, we probably shouldn't fetch, or fetch everything?
        // Based on requirements, we wait for selection. But if triggered manually without ID:
        // Let's assume we need an ID or it returns empty/all. 
        // If we want to enforce selection, we can return early here.
        // However, existing backend supports no filter (returns all).
      }

      const response = await callApi(url, accessToken);
      const data = await response.json();
      if (response.ok) {
        setDashboardData(Array.isArray(data.dashboard_data) ? data.dashboard_data : []);
        setMismatchPatterns(Array.isArray(data.mismatch_patterns) ? data.mismatch_patterns : []);
        // The useEffect will now handle calculating and setting these values, 
        // but we can set the initial state from the API to avoid a flicker.
        if (matchMode === 'exact') {
            setCurrentMismatches(data.current_mismatches || 0);
            setAccuracyPercentage(data.accuracy_percentage || 0);
        }
      } else {
        throw new Error(data.error || 'Failed to fetch dashboard data');
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, status: "error", duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = async (e) => {
    const clientId = e.target.value;
    setSelectedClientId(clientId);
    setConfigIdFilter('');
    setRecentConfigs([]);
    setDashboardData([]); // Clear data when client changes
    setMismatchPatterns([]);

    if (clientId) {
      const configs = await fetchRecentConfigs(clientId);
      if (configs && configs.length > 0) {
        const mostRecentId = configs[0].id.toString();
        setConfigIdFilter(mostRecentId);
        fetchData(mostRecentId);
      } else {
        // If no configs found, maybe clear data or show empty?
        // We already cleared data above.
      }
    }
  };

  const handleFilterClick = () => {
    fetchData();
  };

  const handleRowClick = (caseId) => {
    // Navigate to the review page, passing the current filtered list for navigation context
    navigate(`/cpt-feedback-review/${caseId}`, { 
        state: { 
          caseList: filteredAndSortedData,
          configId: configIdFilter,
          matchMode: matchMode
        } 
    });
  };

  const getBadgeColor = (status) => {
    switch (status) {
      case 'Match': return 'green';
      case 'Level Match': return 'yellow';
      case 'Partial Match': return 'yellow';
      case 'Mismatch': return 'red';
      default: return 'gray';
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending Review': return 'orange';
      case 'Reviewed': return 'green';
      default: return 'gray';
    }
  };

  const totalCases = dashboardData.length;

  return (
    <Box>
        <HeaderMenus />
        <Box p={8}>
            <Flex justify="space-between" align="center" mb={6}>
                <Heading>CPT Coding Review Dashboard</Heading>
                <HStack>
                    <Button colorScheme="teal" onClick={() => navigate('/cpt-feedback-configs')}>
                        View Configuration Accuracy
                    </Button>
                    <Button colorScheme="blue" onClick={() => navigate('/cpt-feedback-results')}>
                        View Review Results
                    </Button>
                </HStack>
            </Flex>

            <VStack mb={6} p={4} borderWidth="1px" borderRadius="lg" align="start">
              {!loading && (
                <HStack spacing="8">
                  <Stat>
                    <StatLabel>Total Cases</StatLabel>
                    <StatNumber>{totalCases}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Current Mismatches</StatLabel>
                    <StatNumber>{currentMismatches}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Accuracy</StatLabel>
                    <StatNumber>{accuracyPercentage}%</StatNumber>
                  </Stat>
                </HStack>
              )}
              <HStack spacing={6}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="show-matches" mb="0">
                    Show Matches
                  </FormLabel>
                  <Switch
                    id="show-matches"
                    isChecked={showMatches}
                    onChange={() => setShowMatches(!showMatches)}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="level-match-mode" mb="0">
                    EM Level Match Mode
                  </FormLabel>
                  <Switch
                    id="level-match-mode"
                    isChecked={matchMode === 'level'}
                    onChange={() => setMatchMode(prev => prev === 'exact' ? 'level' : 'exact')}
                  />
                </FormControl>
              </HStack>
              <VStack mt={4} align="start" spacing={4} width="100%">
                <FormControl maxW="300px">
                  <FormLabel htmlFor="client-select" mb="1">
                    Select Client
                  </FormLabel>
                  <Select
                    id="client-select"
                    value={selectedClientId}
                    onChange={handleClientChange}
                    size="sm"
                    placeholder="Select a client"
                  >
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl maxW="300px" isDisabled={!selectedClientId}>
                  <FormLabel htmlFor="config-id-filter" mb="1">
                    Select Configuration
                  </FormLabel>
                  <Select
                    id="config-id-filter"
                    value={configIdFilter}
                    onChange={(e) => setConfigIdFilter(e.target.value)}
                    size="sm"
                    placeholder="Select configuration"
                  >
                    {recentConfigs.map((config) => (
                      <option key={config.id} value={config.id}>
                        {config.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                
                <Button 
                  onClick={handleFilterClick} 
                  size="sm" 
                  colorScheme="blue"
                  isDisabled={!selectedClientId}
                >
                  Filter
                </Button>
              </VStack>
            </VStack>

            {mismatchPatterns.length > 0 && (
              <Box mb={8} width="fit-content">
                <Heading size="md" mb={4}>Common Mismatch Patterns</Heading>
                <Table variant="simple" size="sm" borderWidth="1px" borderRadius="lg">
                  <Thead>
                    <Tr>
                      <Th maxW="200px">AI Codes</Th>
                      <Th maxW="200px">Manual Codes</Th>
                      <Th isNumeric>Count</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {mismatchPatterns.map((pattern, index) => (
                      <Tr key={index}>
                        <Td>{pattern.ai_codes}</Td>
                        <Td>{pattern.manual_codes}</Td>
                        <Td isNumeric>{pattern.count}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}

            {loading ? (
                <Spinner />
            ) : (
                <Table variant="simple">
                <Thead>
                    <Tr>
                    <Th>External Case ID</Th>
                    <Th>Case Description</Th>
                    <Th>Review Status</Th>
                    <Th>Match Status</Th>
                    <Th>AI Codes</Th>
                    <Th>Manual Codes</Th>
                    <Th>Last Updated</Th>
                    <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {filteredAndSortedData.map((row, index) => {
                      const currentStatusKey = `match_status_${matchMode}`;
                      let matchStatusText = row[currentStatusKey];
                      let matchStatusColor = getBadgeColor(matchStatusText);

                      if (row.review_outcome === 'revedy_correct') {
                        matchStatusText = 'MATCH-REVIEW';
                        matchStatusColor = 'green';
                      }

                      return (
                        <Tr key={row.case_id}>
                            <Td>{row.external_case_id}</Td>
                            <Td>{row.case_description}</Td>
                            <Td>
                              <Badge colorScheme={getStatusColor(row.status)}>
                                {row.status}
                              </Badge>
                            </Td>
                            <Td>
                              <Badge colorScheme={matchStatusColor}>
                                {matchStatusText}
                              </Badge>
                            </Td>
                            <Td>{row.ai_codes.join(', ')}</Td>
                            <Td>{row.manual_codes.join(', ')}</Td>
                            <Td>{new Date(row.last_updated).toLocaleDateString()}</Td>
                            <Td>
                            <Button size="sm" onClick={() => handleRowClick(row.case_id)}>Review</Button>
                            </Td>
                        </Tr>
                      );
                    })}
                </Tbody>
                </Table>
            )}
        </Box>
    </Box>
  );
};

export default ManualCPTDashboard;